













































import Vue from 'vue';
import InfiniteLoading from 'vue-infinite-loading';
import {
  castArray,
  concat,
  debounce,
  every,
  filter,
  find,
  flow,
  has,
  get,
  isNull,
  isString,
  map,
  uniqBy,
} from 'lodash';
import { VBTooltip } from 'bootstrap-vue';

import ZenMultiselect from '@reciprocity/multiselect';

import { idsToFilter, objectTypeToOrder, objectTypeToQuery, searchQueryToFilter } from './graphql/helpers';
import { edgeToGroupDropdownOption, filterEmptyGroups, filterNoAccessPrivateGroups } from './helpers/groups';
import { getTooltipData, getPersonFullLabel } from './helpers/peopleTooltips';
import { fetchMoreObjects } from './ZenObjectsDropdown.queries';

export default Vue.extend({
  name: 'ZenObjectsDropdown',
  components: {
    ZenMultiselect,
    InfiniteLoading,
  },
  directives: {
    BTooltip: VBTooltip,
  },
  props: {
    objectType: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Array as String[]],
      validator: function(value) {
        return isNull(value) || isString(value) || every(value, isString);
      },
      default: () => null,
    },
    placeholder: {
      type: String,
      default: 'Select object',
    },
    pageSize: {
      type: Number,
      default: 25,
    },
  },
  apollo: {
    objects: {
      // TODO: during dev only, remove
      fetchPolicy: 'network-only',

      query() {
        return objectTypeToQuery(this.objectType);
      },
      variables() {
        return {
          objectType: this.objectType,
          pageSize: this.pageSize,
          after: null,
          order: objectTypeToOrder(this.objectType),
          filter: searchQueryToFilter(this.objectType, this.searchQuery),
        };
      },
      update(data) {
        // TODO: TEMP: this must be done using a GQL query param
        if (this.objectType === 'Group') {
          data.objects.edges = filterEmptyGroups(data.objects.edges);
          data.objects.edges = filterNoAccessPrivateGroups(data.objects.edges);
        }

        return data.objects;
      },
      error() {
        // TODO: here we get an error! why?
        this.loadingError = 'Error';
      },
    },
    preselectedObjects: {
      // TODO: during dev only, remove
      fetchPolicy: 'network-only',

      query() {
        return objectTypeToQuery(this.objectType);
      },
      variables() {
        return {
          objectType: this.objectType,
          pageSize: 1000, // give it all at once
          page: 1,
          order: objectTypeToOrder(this.objectType),
          filter: idsToFilter(this.selectedOptions),
        };
      },
      update(data) {
        return data.objects;
      },
    },
  },
  data() {
    return {
      objects: null,
      preselectedObjects: null,

      searchQuery: '',
      loadingError: '',
      preselectedIds: [],
      extraItems: [],
    };
  },
  computed: {
    disabled(): boolean {
      return this.objects === null;
    },
    loading(): boolean {
      return this.$apollo.queries.objects.loading;
    },

    isPeopleType() {
      return this.objectType === 'Person';
    },

    allOptionsLoaded(): boolean {
      if (!has(this.objects, 'pageInfo')) return false;

      return this.objects.pageInfo.endCursor == null;
    },

    labelFn() {
      const labelFn = object => object.label;

      return this.isPeopleType ? getPersonFullLabel : labelFn;
    },

    internalValue() {
      const valueToDropdownOption = (options, value) => find(options, option => option.value === value);
      const valuesToDropdownOptions = values =>
        map(values, selectedOption => valueToDropdownOption(this.options, selectedOption));

      return valuesToDropdownOptions(this.selectedOptions);
    },
    selectedOptions() {
      const castArrayAndStripNulls = flow(
        value => castArray(value),
        values => filter(values, v => v)
      );

      return castArrayAndStripNulls(this.value);
    },
    // TODO: move some of this logic out of component
    options() {
      const edgeToObjectDropdownOption = edge => ({
        value: edge.node.id,
        label: edge.node.sorTitle.value,
        // Extra info for filters
        code: edge.node.sorCode.value,
      });
      const edgeToTagDropdownOption = edge => ({
        value: edge.node.id,
        label: edge.node.title.value,
      });
      const edgeToPeopleDropdownOption = edge => ({
        value: edge.node.id,
        label: edge.node.name.value,
        // Extra info for tooltips
        email: edge.node.email.value,
        role: edge.node.userRole.value,
      });

      const edgeToDropdownOption = (objectType: string) => {
        const objectTypeToDropdownOption = {
          Tag: edgeToTagDropdownOption,
          Group: edgeToGroupDropdownOption,
          Person: edgeToPeopleDropdownOption,
        };

        return get(objectTypeToDropdownOption, objectType, edgeToObjectDropdownOption);
      };
      const toDropdownOptions = edges => {
        if (!edges.length) return [];

        return map(edges, edgeToDropdownOption(this.objectType));
      };

      const preselectedObjectsEdges = get(this.preselectedObjects, 'edges', []);
      const objectsEdges = get(this.objects, 'edges', []);

      const objects = concat(preselectedObjectsEdges, objectsEdges);

      const dropdownOptions = toDropdownOptions(objects);

      return uniqBy(dropdownOptions, 'value');
    },

    internalPlaceholder() {
      return this.loadingError || this.$props.placeholder;
    },
  },
  methods: {
    getPersonFullLabel,
    getTooltipData,

    async fetchMore() {
      if (this.$apollo.loading || this.allOptionsLoaded) return;

      const variables = {
        after: this.objects.pageInfo.endCursor,
      };

      const { result, error } = await fetchMoreObjects(this.$apollo, variables);

      return { result, error };
    },

    // TODO: add support for multiple selected
    onChange(object) {
      // TODO: emit only the UUID after filters support it
      this.$emit('inputLegacy', object);

      const emitValue = object.value;
      this.$emit('input', emitValue);
    },
    onSearchChange: debounce(function(searchQuery) {
      this.searchQuery = searchQuery;
    }, 500),
    async onScrollToBottom($state) {
      const { result, error } = await this.fetchMore();

      // TODO: handle error better, should display a message at the bottom of the dropdown and stop loading
      if (error || !result) return;

      $state.loaded();

      if (this.allOptionsLoaded) {
        $state.complete();
      }
    },
  },
});
